import type { es } from './es'

/** Misma forma que es.ts, con cualquier texto en lugar de los literales: TypeScript avisa si falta una clave. */
type Ancho<T> = T extends string
  ? string
  : T extends (...args: infer A) => infer R
    ? (...args: A) => Ancho<R>
    : T extends readonly (infer U)[]
      ? readonly Ancho<U>[]
      : { readonly [K in keyof T]: Ancho<T[K]> }

export type Textos = Ancho<typeof es>

// Textos de la tienda en inglés (idioma por defecto de la demostración). Mismas claves que es.ts.
export const en: Textos = {
  tituloDocumento: 'Renew MD™ · Makeup and beauty store',
  idioma: { otro: 'es', nombreOtro: 'Español', cambiar: 'View the store in Spanish' },
  marca: 'Renew MD',
  lema: 'Makeup and beauty',
  marcaMarca: '™',
  zona: 'Store',
  saltar: 'Skip to content',
  navegacionPrincipal: 'Main',
  inicio: {
    tituloPagina: 'Renew MD: makeup and beauty with a new perspective',
    carrusel: {
      etiqueta: 'Promotions and collections',
      anterior: 'Previous banner',
      siguiente: 'Next banner',
      pausar: 'Pause autoplay',
      reanudar: 'Resume autoplay',
      posicion: (n: number, total: number) => `${n} of ${total}`,
      irA: (n: number, titulo: string) => `Go to banner ${n}: ${titulo}`,
      diapositivas: [
        {
          etiqueta: 'New collection',
          titulo: 'Makeup and beauty with a new perspective',
          texto: 'Science-inspired innovation for your daily routine. Choose, pay by SPEI transfer and get it delivered to your door.',
          cta: 'Shop now',
          href: '/catalogo',
          foto: '1631214524085-17874764a0e5',
          alt: 'Liquid lipsticks and color swatches on a pink background',
        },
        {
          etiqueta: 'Face',
          titulo: 'Your full routine in five minutes',
          texto: 'Lightweight foundation, blush and pressed powder for a natural finish that lasts all day.',
          cta: 'Shop face products',
          href: '/catalogo?categoria=rostro',
          foto: '1758272421370-4c01b8084130',
          alt: 'Young woman applying makeup in front of a mirror',
        },
        {
          etiqueta: 'For businesses',
          titulo: 'Partner pricing for your salon or store',
          texto: 'Register your business, get verified and buy at your price list.',
          cta: 'Register your business',
          href: '/registro-empresa',
          foto: '1631730486572-226d1f595b68',
          alt: 'Cosmetic bottles and compacts on a pink background',
        },
      ],
    },
    categorias: 'Shop by category',
    novedades: 'New arrivals',
    recomendados: 'Recommended',
    estanteAnterior: (titulo: string) => `See previous in ${titulo}`,
    estanteSiguiente: (titulo: string) => `See more in ${titulo}`,
    categoriasDestacadas: [
      {
        categoria: 'labios',
        etiqueta: 'Lips',
        titulo: 'Color that stays with you',
        texto: 'Long-wearing matte and satin lipsticks and glosses.',
        cta: 'Shop lips',
        estante: 'For your lips',
        foto: '1487412947147-5cebf100ffc2',
        alt: 'Close-up of lips while lipstick is applied',
      },
      {
        categoria: 'ojos',
        etiqueta: 'Eyes and brows',
        titulo: 'A defined look',
        texto: 'Eyeshadow, mascara, eyeliner and brow pencil for every style.',
        cta: 'Shop eyes',
        estante: 'Eyes and brows',
        foto: '1554226629-2af69efaa2fb',
        alt: 'Woman applying shimmery eyeshadow',
      },
      {
        categoria: 'rostro',
        etiqueta: 'Face and skin',
        titulo: 'Cared-for skin, a light finish',
        texto: 'Foundations, powders, highlighters and serum to prep and set.',
        cta: 'Shop face',
        estante: 'Face and skin',
        foto: '1748543669178-efd3de4e64e0',
        alt: 'Drops of clear serum next to green leaves',
      },
    ],
    confianzaEtiqueta: 'How you shop at Renew MD',
    confianza: [
      { titulo: 'Shipping across Mexico', texto: 'With a tracking number on every order.' },
      { titulo: 'Pay by SPEI', texto: 'A unique reference for each order.' },
      { titulo: 'Support in English and Spanish', texto: 'A team ready to help you.' },
    ],
    socio: {
      titulo: 'Do you run a store, salon or distributor?',
      texto: 'Register your business and get verified to see your partner price.',
      cta: 'Register your business',
    },
  },
  ayuda: {
    titulo: 'Help',
    intro: 'How to buy, what makes us different and answers to the most common questions.',
  },
  demo: {
    aviso: 'Demo with sample data',
    detalle: 'Products, prices and orders are fictional: nothing is charged or shipped.',
    verComo: 'View as',
    cliente: 'Customer',
    socio: 'Verified partner',
    equipo: 'Simulate the team',
    sinPaso: 'Place an order to simulate the team’s work',
    verificar: 'Verify my business',
    reiniciar: 'Reset',
    reiniciada: 'The demo is back to its initial state.',
    pasoHecho: 'Done: the order moved forward one step.',
    verificada: 'Your business is verified and you can now see your prices.',
    servidor: {
      banco: 'Sample bank',
      beneficiario: 'Renew MD (sample account)',
      paqueteria: 'Sample carrier',
      notas: {
        creado: 'Order created',
        comprobante: 'Proof of payment received',
        conciliado: 'Payment confirmed',
        preparando: 'Preparing your order',
        enviado: 'Shipped with the carrier',
        entregado: 'Delivered',
        cancelado: 'Canceled by the customer',
      },
      pasos: { conciliar: 'Confirm the payment', preparar: 'Start preparing', enviar: 'Mark as shipped', entregar: 'Mark as delivered' },
      errores: {
        noDisponible: 'A product in your order is no longer available.',
        cantidad: (producto: string, min: number, mult: number) => `The quantity of ${producto} must be at least ${min}, in multiples of ${mult}.`,
        sinPrecio: (producto: string) => `${producto} doesn’t have a current price.`,
        sesion: 'You need to sign in.',
        items: 'An order must have between 1 and 50 products.',
        faltaEntrega: (campo: string) => `Missing delivery detail: ${campo}.`,
        codigoPostal: 'The postal code must have 5 digits.',
        demasiados: 'You have too many unpaid orders. Pay or cancel one before creating another.',
        noComprobante: 'This order no longer accepts proof of payment.',
        noCancelable: 'This order can no longer be canceled.',
        yaEmpresa: 'You already have a registered business.',
        noIncluye: (nombre: string) => `The demo doesn’t include ${nombre}.`,
        generico: 'Something went wrong in the demo.',
      },
    },
  },
  tipoCambio: (tc: string, fecha: string) => `Prices set in US dollars (US$) and converted to Mexican pesos: US$1 = $${tc} MXN as of ${fecha}. The exchange rate is locked when you place your order.`,
  nav: { productos: 'Products', miEmpresa: 'My business', pedidos: 'My orders', carrito: 'Cart', ingresar: 'Sign in', salir: 'Sign out' },
  portada: {
    etiqueta: 'Makeup and beauty',
    titular: 'Today’s makeup',
    acento: 'starts here.',
    intro: 'A new way to discover makeup and beauty, with clear information on every product and support in English and Spanish.',
    explorar: 'Explore products',
    conocer: 'About the brand',
    tarjetas: ['Face', 'Eyes', 'Lips'],
    valores: ['Strategic partnerships', 'Growing markets', 'Innovative products', 'A stronger tomorrow'],
  },
  productos: {
    etiqueta: 'Our products',
    titulo: 'Makeup. Color. A brighter tomorrow.',
    intro: 'Explore the collection. Ask questions. Find clear information for your next step.',
    verTodos: 'View the full catalog',
  },
  areas: {
    etiqueta: 'Categories',
    titulo: 'Six categories. One quality standard.',
    intro: 'A portfolio focused on what you use every day.',
    items: [
      { slug: 'rostro', nombre: 'Face' },
      { slug: 'ojos', nombre: 'Eyes' },
      { slug: 'labios', nombre: 'Lips' },
      { slug: 'cejas', nombre: 'Brows' },
      { slug: 'piel', nombre: 'Skin' },
      { slug: 'accesorios', nombre: 'Accessories' },
    ],
  },
  porque: {
    etiqueta: 'Why Renew MD',
    titulo: 'Quality first.',
    acento: 'Innovation always.',
    items: [
      { titulo: 'Thoughtful sourcing', texto: 'We aim to work with qualified suppliers and prioritize documentation and applicable standards.' },
      { titulo: 'Clear, honest information', texto: 'Accurate details on every product and realistic expectations, so you can choose with confidence.' },
      { titulo: 'English and Spanish', texto: 'Bilingual support so language is never a barrier between you and the information.' },
      { titulo: 'People at the center', texto: 'An attentive team ready to answer questions about products, availability and next steps.' },
    ],
  },
  como: {
    etiqueta: 'How it works',
    titulo: 'Four simple steps to your next decision.',
    pasos: [
      { titulo: 'Explore', texto: 'Browse the collection and learn how each product is used and packaged.' },
      { titulo: 'Create your account', texto: 'Sign in with your email. If you’re a store or partner, register your business.' },
      { titulo: 'We verify partners', texto: 'We review business documents and assign their special pricing.' },
      { titulo: 'Choose with confidence', texto: 'Compare sizes and prices with all the information in view.' },
    ],
    cta: 'Start exploring',
  },
  alianza: {
    etiqueta: 'People. Purpose. Possibility.',
    titulo: 'Let’s build a stronger tomorrow together.',
    texto: 'Connect with Renew MD for business partnerships and market development in Mexico.',
    cta: 'Register my business',
  },
  faq: {
    etiqueta: 'Clear information',
    titulo: 'Good questions. Clear answers.',
    items: [
      { p: 'What is Renew MD?', r: 'A beauty and wellness brand. This store brings together its makeup and the information on each product.' },
      { p: 'How do I see prices?', r: 'Every product shows its list price. Verified businesses also see their special price.' },
      { p: 'How do I become a partner?', r: 'Register your business, upload your documents and the team reviews them. We’ll email you the result.' },
      { p: 'Is the store available in Spanish?', r: 'Yes. Use the language button at the top of the page to switch between English and Spanish.' },
    ],
  },
  mision: {
    etiqueta: 'Our shared mission',
    titulo: 'Quality people. Bigger possibilities.',
    texto: 'Be part of a vision to advance beauty and wellness in Mexico.',
    cta: 'Register my business',
  },
  acceso: {
    titulo: 'Sign in',
    intro: 'Sign in with your email and password, or create an account in a moment.',
    introEnlace: 'Enter your email and we’ll send you a sign-in link. If you don’t have an account yet, it’s created when you open the link.',
    usarEnlace: 'I’d rather sign in with an email link',
    usarPassword: 'I’d rather use my password',
    nuevaTitulo: 'Choose your password',
    nuevaIntro: 'Enter the password you’ll use to sign in to the store.',
    cargando: 'Checking your session…',
    correo: 'Email',
    enviarEnlace: 'Send link',
    enviando: 'Sending…',
    enviadoTitulo: 'Check your email',
    enviadoTexto: 'We sent you a sign-in link. It can take a couple of minutes; check your spam folder too.',
    usarOtroCorreo: 'Use another email',
    errorEnvio: 'We couldn’t send the link. Wait a few minutes and try again.',
  },
  catalogo: {
    titulo: 'Products',
    intro: 'Makeup and beauty from Renew MD. The list price is for everyone; verified businesses see their special price.',
    cargando: 'Loading products…',
    vacioTitulo: 'Products are coming soon',
    vacioTexto: 'We’re preparing the collection. Check back in a few days.',
    errorTitulo: 'We couldn’t load the products',
    errorTexto: 'Check your connection and try again. If the problem continues, contact support.',
    sinConexionTitulo: 'The store isn’t configured in this environment',
    sinConexionTexto: 'The database address or public key is missing.',
    reintentar: 'Try again',
    filtros: 'Filter by category',
    todos: 'All',
    sinImagen: 'No image',
    desde: 'From',
    precioLista: 'List price',
    precioSocio: 'Your partner price',
    sinPrecio: 'Price to be confirmed',
    verProducto: 'View product',
    volver: 'Back to products',
    presentaciones: 'Sizes and shades',
    ingredientes: 'Ingredients',
    modoDeUso: 'How to use',
    iva: 'VAT included in the order’s final price',
    socioInvitacion: 'If you’re a store or partner, register your business to see your special price.',
    noExiste: 'That product doesn’t exist or is no longer available.',
  },
  registro: {
    titulo: 'Register your business',
    intro: 'There are four steps. You can leave and continue later: whatever you save is kept.',
    pasos: ['Tax details', 'Health compliance officer', 'Delivery location', 'Documents'],
    guardando: 'Saving…',
    guardarYSeguir: 'Save and continue',
    eligeOpcion: 'Choose an option',
    atras: 'Back',
    errorGenerico: 'We couldn’t save. Try again in a few minutes.',
    cargando: 'Preparing your registration…',
    necesitaSesion: 'To register your business, sign in with your email first.',
    fiscal: {
      razonSocial: 'Legal business name',
      rfc: 'RFC (Mexican tax ID)',
      ayudaRfc: 'Including the homoclave, for example XAXX010101000. Once saved, only EG can change it.',
      tipo: 'Business type',
      tipos: { farmacia: 'Pharmacy', clinica: 'Clinic', consultorio: 'Medical office', distribuidor: 'Distributor' },
      codigoPostal: 'Tax postal code',
      correo: 'Billing email',
      telefono: 'Phone (optional)',
      rfcRepetido: 'A business with that RFC is already registered. If it’s yours, ask the person who registered it for access.',
      maximo: 'This account has reached the maximum number of registered businesses.',
    },
    sanitario: {
      intro: 'We need at least an operating notice or a health license, plus your health compliance officer.',
      aviso: 'Operating notice number',
      licencia: 'Health license number',
      responsable: 'Health compliance officer’s name',
      cedula: 'Officer’s professional license number',
      faltaAviso: 'Enter the operating notice or the health license.',
    },
    sucursal: {
      alias: 'Location name',
      calle: 'Street',
      numeroExterior: 'Street number',
      numeroInterior: 'Unit number (optional)',
      colonia: 'Neighborhood (colonia)',
      municipio: 'Borough or municipality',
      estado: 'State',
      codigoPostal: 'Postal code',
      contacto: 'Contact person',
      telefono: 'Contact phone',
      horario: 'Receiving hours (optional)',
      referencias: 'Directions (optional)',
    },
    documentos: {
      intro: 'Upload each document as a PDF, JPG or PNG, up to 10 MB. The EG team reviews them.',
      tipos: {
        constancia_situacion_fiscal: 'Tax status certificate',
        aviso_funcionamiento: 'Operating notice',
        licencia_sanitaria: 'Health license',
        cedula_responsable: 'Health compliance officer’s license',
        otro: 'Other document',
      },
      subir: 'Upload file',
      subiendo: 'Uploading…',
      subido: 'Uploaded',
      errorTipo: 'The file must be a PDF, JPG or PNG.',
      errorPeso: 'The file is larger than 10 MB.',
      errorSubida: 'We couldn’t upload the file. Please try again.',
      terminar: 'Finish registration',
      faltan: 'Upload at least the tax status certificate and the officer’s license to finish.',
    },
    privacidad: 'I accept the Renew MD privacy notice.',
    privacidadError: 'To continue, accept the privacy notice.',
    listoTitulo: 'Registration submitted',
    listoTexto: 'EG will review your documents. We’ll email you when the review is done.',
  },
  miEmpresa: {
    titulo: 'My business',
    sinEmpresaTitulo: 'You haven’t registered a business yet',
    sinEmpresaTexto: 'To see prices and buy, register your business and submit your documents for review.',
    registrar: 'Register my business',
    continuar: 'Continue my registration',
    estados: {
      pendiente: 'Under review',
      verificada: 'Verified',
      rechazada: 'Rejected',
      suspendida: 'Suspended',
    },
    estadoTexto: {
      pendiente: 'EG is reviewing your information. In the meantime you can browse the catalog, without prices.',
      verificada: 'Your business is verified. You can now see your prices in the catalog.',
      rechazada: 'EG couldn’t verify your business. Check the reason and fix what it says.',
      suspendida: 'Your business is suspended. Contact EG to resolve it.',
    },
    motivo: 'Reason',
    datos: 'Business details',
    documentos: 'Documents',
    sinDocumentos: 'You haven’t uploaded any documents yet.',
    estadosDocumento: { pendiente: 'Under review', aceptado: 'Accepted', rechazado: 'Rejected' },
  },
  guardado: { titulo: 'Saved', texto: 'Your details were saved.' },
  validacion: {
    razonSocial: 'Enter the full legal business name.',
    rfc: 'The RFC format isn’t valid. Check the letters, date and homoclave.',
    tipoEmpresa: 'Choose the business type.',
    codigoPostal: 'The postal code has 5 digits.',
    correo: 'Enter a valid email, for example invoices@yourbusiness.mx.',
    telefono: 'Use numbers only, 8 to 20 characters.',
    responsable: 'Enter the health compliance officer’s name.',
    cedula: 'The professional license number has 7 or 8 digits.',
    faltaAviso: 'Enter the operating notice or the health license.',
    alias: 'Enter a name for the location, for example “Main office”.',
    calle: 'Enter the street.',
    numeroExterior: 'Enter the street number.',
    colonia: 'Enter the neighborhood.',
    municipio: 'Enter the borough or municipality.',
    estado: 'Enter the state.',
    contacto: 'Enter who receives the orders.',
    nombreRecibe: 'Enter the name of the person receiving the order.',
  },
  errorTitulo: 'Couldn’t complete that',
  carrito: {
    titulo: 'Cart',
    vacioTitulo: 'Your cart is empty',
    vacioTexto: 'Explore the products and add whatever you like.',
    explorar: 'Explore products',
    agregar: 'Add to cart',
    agregado: 'Added to cart',
    verCarrito: 'View cart',
    cantidad: 'Quantity',
    quitar: 'Remove',
    noDisponible: 'This product is no longer available. Remove it to continue.',
    sinPrecio: 'No current price. Remove it to continue.',
    minimo: (min: number, mult: number) => (mult > 1 ? `Minimum ${min}, in multiples of ${mult}` : `Minimum ${min}`),
    precioIvaIncluido: 'VAT included',
    precioMasIva: '+ VAT',
    subtotal: 'Subtotal',
    iva: 'VAT',
    total: 'Estimated total',
    notaEstimado: 'This is an estimate. The store confirms the final total when you place your order.',
    continuar: 'Continue to checkout',
    precioSocio: 'Partner price',
    cargando: 'Preparing your cart…',
  },
  checkout: {
    titulo: 'Delivery details',
    intro: 'Tell us where to deliver your order. Next, we’ll show you how to pay.',
    resumen: 'Order summary',
    crear: 'Place order',
    creando: 'Placing order…',
    necesitaSesion: 'To place your order, sign in with your email. Your cart is saved.',
    cerradas: 'Purchases aren’t open yet. You can fill your cart and come back soon.',
    carritoVacio: 'Your cart is empty.',
    volverCarrito: 'Back to cart',
    campos: {
      nombre: 'Recipient’s name',
      telefono: 'Phone',
      calle: 'Street',
      numeroExterior: 'Street number',
      numeroInterior: 'Unit number (optional)',
      colonia: 'Neighborhood (colonia)',
      municipio: 'Borough or municipality',
      estado: 'State',
      codigoPostal: 'Postal code',
      referencias: 'Directions (optional)',
      notas: 'Order notes (optional)',
    },
    errorCrear: 'We couldn’t place the order. Please try again.',
    errorTitulo: 'The order couldn’t be placed',
    creado: 'Order placed',
  },
  pedidos: {
    titulo: 'My orders',
    intro: 'See the status of each order and how to pay for it.',
    vacioTitulo: 'You don’t have any orders yet',
    vacioTexto: 'Your first order will show up here.',
    necesitaSesion: 'Sign in with your email to see your orders.',
    cargando: 'Loading your orders…',
    error: 'We couldn’t load your orders. Refresh the page.',
    pedido: 'Order',
    volver: 'Back to my orders',
    noExiste: 'That order doesn’t exist or isn’t yours.',
    columnas: { pedido: 'Order', fecha: 'Date', estado: 'Status', total: 'Total' },
    estados: {
      pendiente_pago: 'Awaiting payment',
      pago_en_revision: 'Payment under review',
      pagado: 'Paid',
      surtiendo: 'Preparing',
      enviado: 'Shipped',
      entregado: 'Delivered',
      cancelado: 'Canceled',
    },
    productos: 'Products',
    entrega: 'Delivery',
    historial: 'History',
    resumen: { subtotal: 'Subtotal', iva: 'VAT', envio: 'Shipping', total: 'Total' },
    pagoTitulo: 'How to pay',
    pagoIntro: 'Make an SPEI bank transfer for the exact total and enter this reference in the payment description.',
    referencia: 'Reference',
    monto: 'Amount',
    banco: 'Bank',
    beneficiario: 'Account holder',
    clabe: 'CLABE',
    datosPendientes: 'We don’t have the account details in the store yet. The team will email them to you.',
    subirComprobante: 'Upload proof of payment',
    cambiarComprobante: 'Upload another proof',
    subiendo: 'Uploading…',
    comprobanteTipo: 'The file must be a PDF, JPG or PNG.',
    comprobantePeso: 'The file is larger than 10 MB.',
    comprobanteError: 'We couldn’t upload your proof of payment. Please try again.',
    comprobanteEnviado: 'Proof of payment sent',
    comprobanteTexto: 'We’ll review it soon and let you know once the payment is confirmed.',
    enRevision: 'We received your proof of payment and are reviewing it.',
    cancelar: 'Cancel order',
    cancelarAyuda: 'Press and hold to cancel',
    cancelado: 'Order canceled',
    guia: 'Tracking number',
    paqueteria: 'Carrier',
    errorAccion: 'We couldn’t complete that action. Please try again.',
    errorTitulo: 'Couldn’t complete that',
  },
  noEncontrada: {
    titulo: 'This page doesn’t exist',
    texto: 'Check the address or go back to the products.',
    volver: 'Go to products',
  },
  pie: 'Renew MD. Sold in Mexico. Prices are in Mexican pesos.',
  pieNav: 'Footer',
  pieEnlaces: { productos: 'Products', alianza: 'Partnerships', registro: 'Register your business', ayuda: 'Help and FAQ' },
}
